/**
 * `TutorialTargetResolver` 어댑터 — 영역 id를 화면 좌표 사각형으로 해석.
 *
 * phaser-brick은 Unity식 GO-이름 씬 그래프가 없으므로, design 해상도 레이아웃 + 레이아웃 SSoT
 * 헬퍼(computeGameviewRect·computeBattleTabLayoutForScreen)로 주요 영역의 Rect를 산출한다.
 * 게임뷰/그리드/팔레트는 실제 렌더와 같은 SSoT에서 파생해 하이라이트가 어긋나지 않게 한다.
 * `layout` 미지정 시 design 기준 LAYOUT 상수로 폴백하며 `ShellLayout`과 구조적으로 호환한다.
 */
import type { TutorialTargetResolver, TutorialRect } from "@yjsgames/phaser-tutorial";
import { resolvePanelContentRect } from "@yjsgames/phaser-ui/panel-content";
import { DESIGN, LAYOUT, MODULE_GRID } from "../data/balance.js";
import { computeGameviewRect } from "../ui/gameviewLayout.js";
import {
  computeBattleTabLayoutForScreen,
  TAB_CONTENT_BORDER,
  TAB_CONTENT_PADDING,
  TAB_INNER_PADDING_X,
  TAB_INNER_PADDING_Y,
} from "../ui/battleTabLayout.js";
import { computeModuleGridVisibleRowStart } from "../ui/moduleGridViewport.js";
import { getTextDirection } from "../i18n/i18n.js";
import { computeStatUpgradeLayout } from "../ui/statUpgradeLayout.js";

/** 좌상단 ?(도움말) 버튼 크기 — ShellFrame HELP_TAB_W/H와 동기(변경 시 함께). 튜토리얼 하이라이트 좌표 계산용. */
const HELP_BTN_W = 26;
const HELP_BTN_H = 18;

/** battleTabLayout.ts가 필요로 하는 레이아웃 필드 집합. ShellLayout과 구조적 호환. */
interface LayoutCoords {
  readonly frameInsetX: number;
  readonly hudY: number;
  readonly tabContentTop: number;
  readonly tabBarTop: number;
  readonly tabPanelTop: number;
  readonly tabPanelHeight: number;
  readonly tabPanelBottom: number;
}

export class GameTargetResolver implements TutorialTargetResolver {
  constructor(
    private readonly width: number = DESIGN.width,
    private readonly height: number = DESIGN.height,
    private readonly layout: LayoutCoords = LAYOUT,
  ) {}

  resolve(targetId: string): TutorialRect | null {
    const W = this.width;
    const L = this.layout;
    // frameInsetX SSoT — gameview와 동일 상수(ad-hoc sidePad 재유입 차단).
    const sidePad = L.frameInsetX;
    switch (targetId) {
      case "gameview": {
        // 전투 필드(벽돌 + 발사대) — 실제 게임뷰 9-slice 프레임(outer)과 동일 SSoT에서 파생(어긋남 0).
        const gv = computeGameviewRect(W);
        return { x: gv.outerX, y: gv.outerTop, width: gv.outerW, height: gv.outerH };
      }
      // 탭 콘텐츠 영역(현재 활성 탭 본문) — 좌표는 탭 공통이라 battleContent와
      // 탭별 alias(shopContent/statContent 등)가 모두 같은 사각형을 강조한다.
      case "battleContent":
      case "shopContent":
      case "codexContent":
      case "relicContent":
      case "statContent":
      case "dungeonContent":
      case "rebirthContent":
      case "mergeContent":
        return {
          x: 8,
          y: L.tabContentTop,
          width: W - 16,
          height: L.tabBarTop - L.tabContentTop,
        };
      case "moduleGrid": {
        // 모듈 배치 그리드 visible viewport(5열 x 최대 3행) — BattleTabView와 동일 레이아웃 SSoT.
        const g = computeBattleTabLayoutForScreen(W, L).grid;
        return { x: g.originX, y: g.originY, width: g.size, height: g.height };
      }
      case "moduleStartGrid":
      case "moduleGoalGrid":
      case "moduleFireGrid": {
        // 그리드 밖 Start/Fire endpoint 셀 — ModuleGridView.cellCenter와 같은 산식.
        // moduleGoalGrid는 유저 노출명(목표 셀), moduleFireGrid는 내부 명칭 호환용 alias.
        const g = computeBattleTabLayoutForScreen(W, L).grid;
        const endpoint = targetId === "moduleStartGrid" ? MODULE_GRID.start : MODULE_GRID.fire;
        const step = MODULE_GRID.cellSize + MODULE_GRID.cellGap;
        const visibleRowStart = computeModuleGridVisibleRowStart({
          gridHeight: MODULE_GRID.height,
          visibleRows: MODULE_GRID.visibleRows,
          anchorY: MODULE_GRID.start.y,
        });
        return {
          x: g.originX + endpoint.x * step,
          y: g.originY + (endpoint.y - visibleRowStart) * step,
          width: MODULE_GRID.cellSize,
          height: MODULE_GRID.cellSize,
        };
      }
      case "modulePalette": {
        // 보유 모듈 팔레트 영역 — BattleTabView와 동일 레이아웃 SSoT.
        const p = computeBattleTabLayoutForScreen(W, L).palette;
        return { x: p.x, y: p.y, width: p.width, height: p.height };
      }
      case "modulePaletteCell": {
        // 팔레트 첫 모듈 타일 셀 — 튜토리얼 손가락 드래그 출발점(스크롤 패널 중심이 아닌 실제 셀).
        const c = computeBattleTabLayoutForScreen(W, L).paletteFirstCell;
        return { x: c.x, y: c.y, width: c.width, height: c.height };
      }
      case "statMultiplier":
      case "statTips": {
        // StatUpgradeTabView와 같은 ShellFrame tabContent + computeStatUpgradeLayout 산식.
        const full = resolvePanelContentRect(
          { x: sidePad, y: L.tabPanelTop, width: W - sidePad * 2, height: L.tabPanelHeight },
          {
            border: TAB_CONTENT_BORDER,
            contentPadding: TAB_CONTENT_PADDING,
            innerPadX: TAB_INNER_PADDING_X,
            innerPadY: TAB_INNER_PADDING_Y,
          },
        );
        const layout = computeStatUpgradeLayout(full, getTextDirection(), 5, L.tabPanelBottom);
        return targetId === "statMultiplier" ? layout.multiplier : layout.tip;
      }
      case "helpButton": {
        // 좌상단 ?(도움말) 버튼 강조 — ShellFrame.positionHelpTab과 동일 좌표(접힘 기준 tabPanelTop). RTL이면 우측.
        const cw = W - sidePad * 2;
        const rtl = getTextDirection() === "rtl";
        const cx = rtl ? sidePad + cw - HELP_BTN_W / 2 - 8 : sidePad + HELP_BTN_W / 2 + 8;
        const cy = L.tabPanelTop - HELP_BTN_H / 2 + 3;
        return { x: cx - HELP_BTN_W / 2, y: cy - HELP_BTN_H / 2, width: HELP_BTN_W, height: HELP_BTN_H };
      }
      case "tabbar": // 하단 탭바(2행)
        return {
          x: sidePad,
          y: L.tabBarTop,
          width: W - sidePad * 2,
          height: this.height - L.tabBarTop,
        };
      case "hud": // 상단 HUD(골드/다이아/챕터 칩)
        return { x: sidePad, y: L.hudY - 4, width: W - sidePad * 2, height: 40 };
      default:
        return null;
    }
  }
}
